// src/components/demos/DemosSection.js

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
  Globe, FileSearch, LineChart, Activity,
  ArrowUpRight, ChevronRight, Sparkles, ArrowRight, Loader2
} from 'lucide-react';
import { statusFilters } from '../../data/studiosData';
import { useStudios } from '../../hooks/useStudios';
import './DemosSection.css';

// Icon mapping
const iconMap = {
  Globe,
  FileSearch,
  LineChart,
  Activity
};

// Animation variants
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const featurePanelVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.2 }
  }
};

const DemosSection = () => {
  const [activeStudio, setActiveStudio] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAutoCycling, setIsAutoCycling] = useState(true);
  const autoCycleRef = useRef(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Fetch studios from Contentful
  const { studios, loading, error } = useStudios();

  // Filter studios based on status
  const filteredStudios = studios.filter(studio => {
    if (statusFilter === 'all') return true;
    return studio.status === statusFilter;
  });

  // Initialize active studio when studios load
  useEffect(() => {
    if (studios.length > 0 && !activeStudio) {
      setActiveStudio(studios[0]);
    }
  }, [studios, activeStudio]);

  // Auto-cycle through studios
  useEffect(() => {
    if (!isAutoCycling || filteredStudios.length === 0) return;

    autoCycleRef.current = setInterval(() => {
      setActiveStudio(prev => {
        const currentIndex = filteredStudios.findIndex(s => s.id === prev.id);
        const nextIndex = (currentIndex + 1) % filteredStudios.length;
        return filteredStudios[nextIndex];
      });
    }, 5000);

    return () => {
      if (autoCycleRef.current) {
        clearInterval(autoCycleRef.current);
      }
    };
  }, [isAutoCycling, filteredStudios]);

  // Reset active studio when filter changes
  useEffect(() => {
    if (filteredStudios.length > 0 && activeStudio) {
      const isActiveInFiltered = filteredStudios.some(s => s.id === activeStudio.id);
      if (!isActiveInFiltered) {
        setActiveStudio(filteredStudios[0]);
      }
    }
  }, [statusFilter, filteredStudios, activeStudio]);

  // Handle manual studio selection
  const handleStudioSelect = (studio) => {
    setActiveStudio(studio);
    setIsAutoCycling(false); // Stop auto-cycle on manual selection

    // Resume auto-cycle after 10 seconds of inactivity
    if (autoCycleRef.current) {
      clearInterval(autoCycleRef.current);
    }
    setTimeout(() => setIsAutoCycling(true), 10000);
  };

  // Get icon component
  const getIcon = (iconName, size = 24) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent size={size} strokeWidth={1.5} /> : null;
  };

  // Loading state
  if (loading) {
    return (
      <section ref={ref} className="demos-section" id="demos">
        <div className="demos-section__bg">
          <div className="demos-section__gradient" />
          <div className="demos-section__grid" />
        </div>
        <div className="demos-section__loading">
          <Loader2 size={32} className="demos-section__spinner" />
          <span>Loading studios...</span>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section ref={ref} className="demos-section" id="demos">
        <div className="demos-section__bg">
          <div className="demos-section__gradient" />
          <div className="demos-section__grid" />
        </div>
        <div className="demos-section__error">
          <span>Unable to load studios</span>
        </div>
      </section>
    );
  }

  // Don't render until activeStudio is set
  if (!activeStudio) {
    return null;
  }

  return (
    <section ref={ref} className="demos-section" id="demos">
      {/* Background */}
      <div className="demos-section__bg">
        <div className="demos-section__gradient" />
        <div className="demos-section__grid" />
      </div>

      <div className="demos-section__content">
        <div className="demos-section__layout">
          {/* Left Sidebar - Sticky */}
          <motion.div
            className="demos-section__sidebar"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            <motion.span className="demos-section__badge" variants={itemVariants}>
              <Sparkles size={14} />
              Interactive Studios
            </motion.span>

            <motion.h2 className="demos-section__title" variants={itemVariants}>
              Live Product
              <span className="demos-section__title-gradient"> Demos</span>
            </motion.h2>

            <motion.p className="demos-section__subtitle" variants={itemVariants}>
              Explore interactive prototypes built on real industrial problems.
              Click any studio to see it in action.
            </motion.p>

            {/* Status Filters */}
            <motion.div className="demos-section__filters" variants={itemVariants}>
              {statusFilters.map(filter => (
                <button
                  key={filter.id}
                  className={`demos-section__filter-pill ${
                    statusFilter === filter.id ? 'demos-section__filter-pill--active' : ''
                  }`}
                  onClick={() => setStatusFilter(filter.id)}
                >
                  {filter.id === 'live' && <span className="demos-section__filter-dot demos-section__filter-dot--live" />}
                  {filter.id === 'in-lab' && <span className="demos-section__filter-dot demos-section__filter-dot--lab" />}
                  {filter.id === 'coming-soon' && <span className="demos-section__filter-dot demos-section__filter-dot--soon" />}
                  {filter.label}
                </button>
              ))}
            </motion.div>

            {/* Compact Studio Selector */}
            <motion.div className="demo-selector" variants={itemVariants}>
              {filteredStudios.map((studio) => (
                <button
                  key={studio.id}
                  className={`demo-selector__item ${
                    activeStudio.id === studio.id ? 'demo-selector__item--active' : ''
                  }`}
                  style={{ '--accent': studio.accent }}
                  onClick={() => handleStudioSelect(studio)}
                  title={studio.name}
                >
                  {getIcon(studio.icon, 20)}
                  {activeStudio.id === studio.id && (
                    <motion.div
                      className="demo-selector__indicator"
                      layoutId="activeIndicator"
                    />
                  )}
                </button>
              ))}
            </motion.div>

            {/* Active Studio Name */}
            <motion.div className="demo-selector__label" variants={itemVariants}>
              <span
                className="demo-selector__label-name"
                style={{ color: activeStudio.accent }}
              >
                {activeStudio.name}
              </span>
              <span className={`demo-selector__label-status demo-selector__label-status--${activeStudio.status}`}>
                <span className="demo-selector__label-dot" />
                {activeStudio.status === 'live' ? 'Live' : activeStudio.status === 'in-lab' ? 'In Lab' : 'Soon'}
              </span>
            </motion.div>

            {/* View All Studios Link */}
            <motion.div variants={itemVariants}>
              <Link to="/demos" className="demos-section__view-all">
                View All Studios
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>

          {/* Right Main Content - Feature Panel */}
          <div className="demos-section__main">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStudio.id}
                className="demo-feature-panel"
                style={{ '--accent': activeStudio.accent }}
                variants={featurePanelVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Preview Area - Clickable when demo available */}
                {activeStudio.demoUrl ? (
                  <a
                    href={activeStudio.demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="demo-feature-panel__preview demo-feature-panel__preview--clickable"
                  >
                    <div className="demo-feature-panel__browser">
                      <div className="demo-feature-panel__browser-dots">
                        <span /><span /><span />
                      </div>
                      <div className="demo-feature-panel__browser-url">
                        {activeStudio.demoUrl.replace('https://', '')}
                      </div>
                    </div>
                    <div className="demo-feature-panel__screen">
                      {activeStudio.thumbnail ? (
                        <img
                          src={activeStudio.thumbnail}
                          alt={`${activeStudio.name} preview`}
                          className="demo-feature-panel__thumbnail"
                        />
                      ) : (
                        <>
                          <div className="demo-feature-panel__icon-large">
                            {getIcon(activeStudio.icon)}
                          </div>
                          <span className="demo-feature-panel__screen-label">
                            {activeStudio.name}
                          </span>
                        </>
                      )}
                    </div>
                  </a>
                ) : (
                  <div className="demo-feature-panel__preview">
                    <div className="demo-feature-panel__browser">
                      <div className="demo-feature-panel__browser-dots">
                        <span /><span /><span />
                      </div>
                      <div className="demo-feature-panel__browser-url">
                        {`${activeStudio.id}.rosalytics.com`}
                      </div>
                    </div>
                    <div className="demo-feature-panel__screen">
                      <div className="demo-feature-panel__icon-large">
                        {getIcon(activeStudio.icon)}
                      </div>
                      <span className="demo-feature-panel__screen-label">
                        {activeStudio.name}
                      </span>
                    </div>
                  </div>
                )}

                {/* Content Area */}
                <div className="demo-feature-panel__content">
                  <div className="demo-feature-panel__header">
                    <span className={`status-chip status-chip--${activeStudio.status}`}>
                      {activeStudio.statusLabel}
                    </span>
                    <span className="demo-feature-panel__domain">
                      {activeStudio.domainLabel}
                    </span>
                  </div>

                  <h3 className="demo-feature-panel__title">{activeStudio.name}</h3>
                  <p className="demo-feature-panel__tagline">{activeStudio.tagline}</p>
                  <p className="demo-feature-panel__description">{activeStudio.shortDescription}</p>

                  <ul className="demo-feature-panel__bullets">
                    {activeStudio.bullets.map((bullet, i) => (
                      <li key={i}>
                        <ChevronRight size={14} style={{ color: activeStudio.accent }} />
                        {bullet}
                      </li>
                    ))}
                  </ul>

                  {/* Tech Stack */}
                  {activeStudio.techStack && (
                    <div className="demo-feature-panel__tech-stack">
                      <span className="demo-feature-panel__tech-label">Tech Stack</span>
                      <div className="demo-feature-panel__tech-tags">
                        {activeStudio.techStack.map((tech, i) => (
                          <span key={i} className="demo-feature-panel__tech-tag">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="demo-feature-panel__actions">
                    {activeStudio.demoUrl ? (
                      <a
                        href={activeStudio.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="demo-feature-panel__btn demo-feature-panel__btn--primary"
                        style={{ background: activeStudio.accent }}
                      >
                        Open Live Demo
                        <ArrowUpRight size={16} />
                      </a>
                    ) : (
                      <button
                        className="demo-feature-panel__btn demo-feature-panel__btn--secondary"
                        disabled
                      >
                        {activeStudio.status === 'coming-soon' ? 'Coming Soon' : 'In Development'}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemosSection;
